/**
 * Ingredient Details — Section 2
 * ------------------------------------------------------------
 * This section never talks to Section 1 directly. It only:
 *
 *   1. Listens on `document` for the "ingredientChanged"
 *      CustomEvent (dispatched by Section 1's cards, and by
 *      this section's own prev/next/dot controls) and shows
 *      the matching `[data-ingredient-panel]`, hiding the rest.
 *
 *   2. On first paint, checks `window.VenatiIngredients`
 *      (exposed by Section 1, if present) to synchronously pick
 *      up the currently active ingredient — this avoids any
 *      flash of the wrong ingredient if Section 1's own
 *      load-time event fires before this section's listener is
 *      attached. If that registry isn't available (e.g. this
 *      section is used standalone, without Section 1), it falls
 *      back to `data-default-ingredient` / the first block.
 *
 * No fetch, no AJAX, no localStorage, no URL params — matching
 * content is looked up purely from the DOM already rendered by
 * Liquid, so adding/removing "Ingredient" blocks in the Theme
 * Editor never requires touching this file.
 */

(function () {
  'use strict';

  var EVENT_NAME = 'ingredientChanged';
  var SELECTOR = '[data-ingredient-details]';
  var PANEL_SELECTOR = '[data-ingredient-panel]';
  var DOT_SELECTOR = '[data-ingredient-dot]';

  function showIngredient(root, ingredientId) {
    if (!ingredientId) {
      return;
    }

    var panels = root.querySelectorAll(PANEL_SELECTOR);
    var matched = false;

    panels.forEach(function (panel) {
      var isMatch = panel.getAttribute('data-ingredient-id') === ingredientId;
      if (isMatch) {
        matched = true;
      }
    });

    // If this section has no block for the given ingredient id,
    // leave the current panel exactly as it is rather than
    // hiding everything.
    if (!matched) {
      return;
    }

    panels.forEach(function (panel) {
      var isMatch = panel.getAttribute('data-ingredient-id') === ingredientId;

      if (isMatch) {
        panel.hidden = false;
        panel.classList.add('is-active');
      } else {
        panel.hidden = true;
        panel.classList.remove('is-active');
      }
    });

    var dots = root.querySelectorAll(DOT_SELECTOR);
    dots.forEach(function (dot) {
      var isMatch = dot.getAttribute('data-ingredient-id') === ingredientId;
      dot.classList.toggle('is-active', isMatch);
      dot.setAttribute('aria-selected', String(isMatch));
    });

    root.setAttribute('data-current-ingredient', ingredientId);
  }

  function getPanelIds(root) {
    var panels = root.querySelectorAll(PANEL_SELECTOR);
    var ids = [];
    panels.forEach(function (panel) {
      ids.push(panel.getAttribute('data-ingredient-id'));
    });
    return ids;
  }

  function currentIngredient(root) {
    return root.getAttribute('data-current-ingredient');
  }

  function dispatchChange(root, ingredientId) {
    document.dispatchEvent(
      new CustomEvent(EVENT_NAME, {
        bubbles: true,
        detail: {
          ingredient: ingredientId,
          sectionId: root.getAttribute('data-section-id')
        }
      })
    );
  }

  function step(root, direction) {
    var ids = getPanelIds(root);
    if (!ids.length) {
      return;
    }

    var current = currentIngredient(root);
    var index = ids.indexOf(current);
    if (index === -1) {
      index = 0;
    }

    var nextIndex = (index + direction + ids.length) % ids.length;
    var nextId = ids[nextIndex];

    showIngredient(root, nextId);
    dispatchChange(root, nextId);
  }

  function resolveInitialIngredient(root) {
    // 1. Ask Section 1's registry, if it exists, for the truly
    //    current state (works even if this section initializes
    //    after Section 1 already dispatched its load event).
    if (window.VenatiIngredients && typeof window.VenatiIngredients.getActive === 'function') {
      var active = window.VenatiIngredients.getActive();
      if (active && active.ingredient) {
        var ids = getPanelIds(root);
        if (ids.indexOf(active.ingredient) !== -1) {
          return active.ingredient;
        }
      }
    }

    // 2. Fall back to the section's configured default.
    var configuredDefault = root.getAttribute('data-default-ingredient');
    var availableIds = getPanelIds(root);
    if (configuredDefault && availableIds.indexOf(configuredDefault) !== -1) {
      return configuredDefault;
    }

    // 3. Fall back to whichever panel is already marked active
    //    (rendered server-side as the first block), or the first
    //    panel in the DOM.
    var activePanel = root.querySelector(PANEL_SELECTOR + '.is-active');
    if (activePanel) {
      return activePanel.getAttribute('data-ingredient-id');
    }

    return availableIds.length ? availableIds[0] : null;
  }

  function initSection(root) {
    if (!root || root.__ingredientDetailsInit) {
      return;
    }
    root.__ingredientDetailsInit = true;

    var initialId = resolveInitialIngredient(root);
    if (initialId) {
      showIngredient(root, initialId);
    }

    var prevButton = root.querySelector('[data-ingredient-prev]');
    var nextButton = root.querySelector('[data-ingredient-next]');

    if (prevButton) {
      prevButton.addEventListener('click', function () {
        step(root, -1);
      });
    }

    if (nextButton) {
      nextButton.addEventListener('click', function () {
        step(root, 1);
      });
    }

    root.addEventListener('click', function (event) {
      var dot = event.target.closest(DOT_SELECTOR);
      if (!dot || !root.contains(dot)) {
        return;
      }
      var ingredientId = dot.getAttribute('data-ingredient-id');
      if (ingredientId === currentIngredient(root)) {
        return;
      }
      showIngredient(root, ingredientId);
      dispatchChange(root, ingredientId);
    });
  }

  function initAll(context) {
    context = context || document;
    var sections = context.querySelectorAll(SELECTOR);
    sections.forEach(initSection);
  }

  // The single integration point with Section 1 (or any other
  // section that dispatches this event).
  document.addEventListener(EVENT_NAME, function (event) {
    var ingredientId = event && event.detail && event.detail.ingredient;
    if (!ingredientId) {
      return;
    }

    document.querySelectorAll(SELECTOR).forEach(function (root) {
      showIngredient(root, ingredientId);
    });
  });

  document.addEventListener('DOMContentLoaded', function () {
    initAll(document);
  });

  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.matches
      ? (event.target.matches(SELECTOR) ? event.target : event.target.querySelector(SELECTOR))
      : null;
    if (root) {
      root.__ingredientDetailsInit = false;
      initSection(root);
    }
  });

  if (document.readyState === 'interactive' || document.readyState === 'complete') {
    initAll(document);
  }
})();
